import type { MouseEvent, ReactNode } from 'react'

import { useFilterQuery } from '../TableFilterContext'
import { FilteredEmpty } from './FilteredEmpty'
import { ListDisclosure } from './ListDisclosure'

/**
 * The admin's one table (M23-T03), drawn on five frames: a `bg-bg-sunken`
 * header row, a rounded card, row dividers, two-line cells, and a right-hand
 * action column.
 *
 * **It renders loading, error and empty as well as rows.** The frames draw none
 * of the three (M23 gap B3, and M16's G31 before it: a scan of all 6,467 node
 * names found zero matches for loading, skeleton, empty, error or disabled).
 * `designs/` already has all of them. A table that can only render happy rows
 * is not finished, and this is the criterion the milestone README calls the one
 * most likely to be silently dropped — so it lives in the shared component
 * where a page ticket cannot forget it.
 *
 * The two empty states are deliberately different. "Nothing has arrived" and
 * "your filter excludes everything" need different words and different exits.
 */

export type Column<T> = {
  key: string
  header: string
  cell: (row: T) => ReactNode
  /** Right-align, for money and actions. */
  align?: 'right'
}

export function DataTable<T>({
  rows,
  columns,
  getKey,
  searchText,
  caption,
  isPending = false,
  isError = false,
  errorMessage,
  onRetry,
  empty,
  paginated = false,
  total,
  onRowClick,
  footer,
}: {
  rows: readonly T[]
  columns: readonly Column<T>[]
  getKey: (row: T) => string
  /** What the filter box matches against. Omit to make the table unfilterable. */
  searchText?: (row: T) => string
  caption: string
  isPending?: boolean
  isError?: boolean
  errorMessage?: string
  onRetry?: () => void
  /** Shown when the source has no rows at all — an `EmptyState`, usually. */
  empty?: ReactNode
  /** True when rows beyond this page exist, so the filter says what it covers. */
  paginated?: boolean
  /** Rows matching the same scope and filter before the server's cap (M24-T03).
   *  When it exceeds `rows.length` the table discloses that it is a window, and
   *  the header filter relabels itself — so a capped page cannot forget either
   *  by forgetting to also pass `paginated`. */
  total?: number | undefined
  onRowClick?: (row: T) => void
  footer?: ReactNode
}) {
  // A capped list IS a partial list, so it must relabel the header filter for
  // the same reason a cursor-paginated one does. Deriving it here rather than
  // asking the page to pass `paginated` too removes the way to get it half
  // right — which is how Audit log and Enquiries ended up the only two pages
  // telling the truth after M23.
  const truncated = total !== undefined && total > rows.length
  const query = useFilterQuery(searchText !== undefined && (paginated || truncated))
  const q = searchText === undefined ? '' : query.trim().toLowerCase()
  const visible =
    q === '' ? rows : rows.filter((r) => searchText!(r).toLowerCase().includes(q))

  if (isError) {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-4 rounded-2xl border border-bed-bad/40 bg-bed-bad-bg px-5 py-4"
      >
        <p className="text-sm font-medium text-bed-bad">
          {errorMessage ?? 'Could not load this list.'}
        </p>
        {onRetry !== undefined && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-9 shrink-0 items-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Retry
          </button>
        )}
      </div>
    )
  }

  if (isPending) {
    return (
      <div
        role="status"
        aria-label={`Loading ${caption.toLowerCase()}`}
        className="overflow-hidden rounded-2xl border border-line-1"
      >
        <div className="h-11 bg-bg-sunken" />
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 border-t border-line-1 px-5 py-4">
            <div className="h-4 w-24 animate-pulse rounded bg-bg-sunken" />
            <div className="h-4 flex-1 animate-pulse rounded bg-bg-sunken" />
            <div className="h-4 w-20 animate-pulse rounded bg-bg-sunken" />
          </div>
        ))}
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-line-1">
        {empty ?? (
          <p className="px-6 py-14 text-center text-sm text-ink-3">
            Nothing here yet.
          </p>
        )}
      </div>
    )
  }

  return (
    // `min-w-0` on the wrapper as well as the inner container: a flex item
    // defaults to `min-width: auto` and refuses to shrink below its widest
    // child, which is how seven of twelve admin routes overflowed at 390 in
    // M23-T15. The disclosure line wraps; it must never widen the page.
    <div className="min-w-0 space-y-3">
      <ListDisclosure shown={rows.length} total={total} />
        <div className="overflow-hidden rounded-2xl border border-line-1">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{caption}</caption>
            <thead>
              <tr className="bg-bg-sunken text-left">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={`px-5 py-3 font-medium text-ink-2 ${
                      c.align === 'right' ? 'text-right' : ''
                    }`}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr
                  key={getKey(row)}
                  {...(onRowClick !== undefined && {
                    onClick: (e: MouseEvent<HTMLTableRowElement>) => {
                      // A click on a control in the row belongs to that control.
                      // Without this, dialling a patient also opens the drawer,
                      // and every page would have to remember to stop it.
                      if (
                        (e.target as HTMLElement).closest(
                          'a, button, input, select, textarea, label',
                        )
                      ) {
                        return
                      }
                      onRowClick(row)
                    },
                    className:
                      'cursor-pointer border-t border-line-1 align-middle hover:bg-bg-soft',
                  })}
                  {...(onRowClick === undefined && {
                    className: 'border-t border-line-1 align-middle',
                  })}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-5 py-4 text-ink-1 ${
                        c.align === 'right' ? 'text-right' : ''
                      }`}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* The sentence itself lives in `FilteredEmpty` so this page and the
            card lists cannot drift apart on it (M25-T01). */}
        {visible.length === 0 && (
          <FilteredEmpty
            query={query}
            scope={
              paginated || truncated
                ? 'This filters the rows loaded so far, not the whole list.'
                : null
            }
          />
        )}

        {footer !== undefined && (
          <div className="border-t border-line-1 px-5 py-3">{footer}</div>
        )}
      </div>
    </div>
  )
}
